import { joinPaths } from './utils';
import {
  Configuration,
  InjectionMode,
  InitParams,
  EventHandlers,
  EventHandler,
} from './schema';
import { extractOnboardingData, getFrameUrl } from './utils/platform';

const IFRAME_READY_TIMEOUT = 30000; // ms

declare const IdCheckIoCore: any;

let registeredListener;

function injectScript(id: string, src: string) {
  return new Promise((resolve, reject) => {
    const head = document.getElementsByTagName('head')[0];
    if (document.getElementById(id)) {
      // the script has already been injected
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;
    script.onload = () => resolve();
    script.addEventListener('error', () => reject('Error loading script.'));
    script.addEventListener('abort', () => reject('Script loading aborted.'));
    head.appendChild(script);
  });
}

/**
 * Wait for iframe.contentWindow to be initialized.
 * iframe.contentWindow is only initialized once the webpage has been fully
 * loaded in the iframe. So, we need to wait for it to be available before
 * we can be able to call the postMessage() API.
 * @param iframe: the iframe element
 * @param startTs: timer start duration
 * @returns {Promise<any>}
 */
function waitForIframeReady(
  iframe: HTMLIFrameElement,
  startTs = new Date().getTime()
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (iframe.contentWindow) {
      resolve();
    } else if (new Date().getTime() - startTs > IFRAME_READY_TIMEOUT) {
      reject(`iframe ready timeout.`);
    } else {
      setTimeout(() => {
        waitForIframeReady(iframe, startTs).then(resolve).catch(reject);
      }, 10);
    }
  });
}

class ScriptLoader {
  private readonly cdnUrl: string;

  constructor(platformUrl: string) {
    this.cdnUrl = joinPaths(platformUrl, '/assets/js');
  }

  injectConfig() {
    return injectScript('idcheckio-sdk-config', `${this.cdnUrl}/config.js`);
  }

  injectCore() {
    return injectScript(
      'idcheckio-sdk-core',
      `${this.cdnUrl}/idcheckio-sdk-core-stable.js`
    );
  }

  injectLang(lang) {
    return injectScript(
      `axt-idcheckio-i18n-${lang}`,
      `${this.cdnUrl}/i18n-${lang}.min.js`
    );
  }

  injectWidget(id) {
    switch (id) {
      case 'LIVENESS':
        return injectScript(
          'axt-liveness-script',
          `${this.cdnUrl}/axt-liveness-stable.js`
        );
      case 'CAMERA_CAPTURE':
        return injectScript(
          'axt-camera-capture-script',
          `${this.cdnUrl}/axt-camera-capture.js`
        );
      default:
        return new Promise((resolve, reject) => {
          reject(new Error(`Cannot find widget ${id}.`));
        });
    }
  }
}

class IdCheckIo {
  private readonly config: Configuration;
  private readonly eventHandlers: EventHandlers;
  private readonly mode: InjectionMode;
  private scriptLoader?: ScriptLoader;
  private iframe?: HTMLIFrameElement;
  private onReadyCallback?: EventHandler;
  private instance: any;

  constructor(config: Configuration) {
    this.config = config || {};
    this.eventHandlers = {};
    this.mode = this.config.mode
      ? (this.config.mode.toUpperCase() as InjectionMode)
      : 'IFRAME';
    // fix iframe listeners when multiple instance
    // remove previous listener
    this.handleMessage = this.handleMessage.bind(this);
    if (registeredListener) {
      window.removeEventListener('message', registeredListener);
    }
  }

  public on(eventName: string, callback: EventHandler) {
    if (eventName === 'ready') {
      this.onReadyCallback = callback;
    } else if (this.instance) {
      this.instance.on(eventName, callback);
    } else {
      this.eventHandlers[eventName] = callback;
    }
  }

  /**
   * Initialize sdkweb element
   * @param { element, uid } onboarding parameters
   */
  public init(params: InitParams) {
    if (this.mode === 'DOM') {
      this.initLibrary(params);
    } else {
      this.initFrame(params);
    }
  }

  private initFrame(params: InitParams) {
    // listen for iframe messages
    this.mapFrameEvents();
    // create an iframe
    this.iframe = document.createElement('iframe');
    this.iframe.setAttribute('src', getFrameUrl(params, this.config.platform));
    this.iframe.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin  allow-top-navigation allow-modals allow-popups'
    );
    this.iframe.setAttribute('allow', 'camera; microphone;');
    this.iframe.setAttribute('width', '100%');
    this.iframe.setAttribute('height', '100%');
    this.iframe.setAttribute('border', 'none');
    this.iframe.style.border = 'none';
    const elmt =
      typeof params.element === 'string'
        ? document.querySelector(params.element)
        : params.element;
    elmt.innerHTML = '';
    elmt.appendChild(this.iframe);
  }

  private mapFrameEvents() {
    registeredListener = this.handleMessage;
    window.addEventListener('message', registeredListener);
  }
  private handleMessage(event) {
    if (event && event.data && event.data.idcheckio) {
      if (event.data.idcheckio.event === 'IFRAME_READY' && this.iframe) {
        // send configuration to iframe
        const msg = {
          idcheckio: {
            appId: 'Sdk-Web',
            resourceType: 'ONBOARDING',
            event: 'IFRAME_CONFIGURATION',
            data: { ...this.config },
          },
        };
        waitForIframeReady(this.iframe).then(() =>
          this.iframe.contentWindow.postMessage(msg, '*')
        );
      } else {
        // trigger event callbacks
        const {
          idcheckio: { event: type, ...data },
        } = event.data;
        const callback = this.eventHandlers[type];
        if (callback) {
          callback(data);
        }
      }
    }
  }

  private initLibrary(params: InitParams) {
    const { uid, platformUrl } = extractOnboardingData(
      params,
      this.config.platform
    );
    this.scriptLoader = new ScriptLoader(platformUrl);
    this.scriptLoader.injectConfig().then(() => {
      const remoteConfig = (window as any).IdCheckIoConfig;
      this.scriptLoader.injectCore().then(() => {
        this.instance = new IdCheckIoCore({
          ...remoteConfig,
          ...this.config,
        });
        Object.keys(this.eventHandlers).forEach((key) => {
          this.instance.on(key, this.eventHandlers[key]);
        });
        this.instance.onLoadWidget((id) => this.scriptLoader.injectWidget(id));
        this.instance.onLoadLanguage((lang) =>
          this.scriptLoader.injectLang(lang)
        );
        this.instance.init({ uid, element: params.element });
        if (this.onReadyCallback) {
          this.onReadyCallback();
        }
      });
    });
  }
}

export default IdCheckIo;
